import { useEffect, useState } from 'react';
import { readTrust } from '@/lib/mfa-trust';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { startMemberSession } from '@/lib/agencyMember';
import { parkChallenge } from '@/Login';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

/**
 * Google OAuth callback landing page.
 *
 * Google bounces the user here with `?code=<auth_code>&state=<jwt>` (or
 * `?error=access_denied` if they refused). We:
 *
 *   1. Read `code` and `state` from the URL
 *   2. POST { code } to /api/google-auth/callback, authenticated with the JWT
 *      from `state` (which we put there when we kicked off the flow)
 *   3. On success → bounce to /settings with the Calendar tab active
 *   4. On failure → show the error and a "Try again" button
 */
export default function OAuthGoogleCallback() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('exchanging'); // exchanging | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state'); // JWT we round-tripped
    const errorParam = params.get('error');

    if (errorParam) {
      setPhase('error');
      setMessage(`Google denied access: ${errorParam}`);
      return;
    }

    if (!code) {
      setPhase('error');
      setMessage('Missing authorization code in callback URL.');
      return;
    }

    // Platform email connect: the state JWT is marked kind='platform_email', so
    // route the code to the PLATFORM endpoint (its own tokens), not a tenant.
    let stateKind = null;
    try {
      stateKind = JSON.parse(atob(String(state).split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).kind;
    } catch (_) { /* not a decodable JWT - treat as tenant flow */ }
    // Signing in (or signing up) with Google. Unauthenticated by definition:
    // there is no token yet, that is the point of the round trip.
    if (stateKind === 'signin') {
      axios
        .post(`${API_URL}/api/auth/google/callback`, { code, state, mfa_trust: readTrust() })
        .then(({ data }) => {
          if (data.needs_workspace) {
            // Google proved who they are; only the company name is left.
            setPhase('success');
            setMessage('Signed in with Google. One more thing\u2026');
            sessionStorage.setItem('gsignup', JSON.stringify({ ticket: data.ticket, email: data.email, name: data.name }));
            setTimeout(() => navigate('/signup?google=1'), 600);
            return;
          }
          // EXACTLY what Login.js stores, through the same helper for agency
          // members - a second idea of what a session is would log people in
          // to a half-built one.
          // Google was the first factor. The second is asked for on the login
          // page, which owns those screens.
          if (data.mfa_required || data.mfa_setup_required) {
            parkChallenge(data);
            navigate('/');
            return;
          }
          if (data.agency_session) {
            startMemberSession(data.token, data.member);
            setPhase('success');
            setMessage('Signed in. Choose a workspace\u2026');
            setTimeout(() => window.location.assign('/app'), 600);
            return;
          }
          localStorage.setItem('token', data.token);
          localStorage.setItem('userId', data.user.id);
          localStorage.setItem('userName', data.user.name);
          setPhase('success');
          setMessage('Signed in. Taking you in\u2026');
          setTimeout(() => window.location.assign('/app'), 600);
        })
        .catch((err) => {
          setPhase('error');
          setMessage(err.response?.data?.error || err.message || 'Google sign-in failed.');
        });
      return;
    }

    if (stateKind === 'platform_email') {
      const ptoken = localStorage.getItem('platform_token');
      if (!ptoken) {
        setPhase('error');
        setMessage('Log in to the superadmin console, then reconnect Google.');
        return;
      }
      axios
        .post(`${API_URL}/api/platform/settings/email/google/callback`, { code, state },
          { headers: { Authorization: `Bearer ${ptoken}` } })
        .then(() => {
          setPhase('success');
          setMessage('Google connected for platform email. Bouncing to the console…');
          setTimeout(() => navigate('/superadmin'), 1500);
        })
        .catch((err) => {
          setPhase('error');
          setMessage(err.response?.data?.error || err.message || 'Token exchange failed.');
        });
      return;
    }

    // Fall back to localStorage token if the OAuth flow didn't carry state
    // (e.g. older "Connect" button that didn't pass &token=…)
    const token = state || localStorage.getItem('token');
    if (!token) {
      setPhase('error');
      setMessage('You need to be logged in to complete the OAuth flow. Please log in and reconnect.');
      return;
    }

    axios
      .post(
        `${API_URL}/api/google-auth/callback`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setPhase('success');
        setMessage('Google Calendar connected. Taking you back to Integrations…');
        setTimeout(() => navigate('/integrations?open=google_calendar'), 1500);
      })
      .catch((err) => {
        setPhase('error');
        setMessage(err.response?.data?.error || err.message || 'Token exchange failed.');
      });
  }, [navigate]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-4 p-8 text-center">
          {phase === 'exchanging' && (
            <>
              <Loader2 className="mx-auto size-8 animate-spin text-primary" />
              <h2 className="text-base font-semibold">Connecting Google Calendar…</h2>
              <p className="text-sm text-muted-foreground">
                Exchanging authorization code for tokens. This takes a second.
              </p>
            </>
          )}
          {phase === 'success' && (
            <>
              <CheckCircle2 className="mx-auto size-8 text-success" />
              <h2 className="text-base font-semibold">Connected</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
            </>
          )}
          {phase === 'error' && (
            <>
              <AlertCircle className="mx-auto size-8 text-destructive" />
              <h2 className="text-base font-semibold">Couldn't finish OAuth</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
              <div className="flex justify-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/integrations?open=google_calendar')}>
                  Back to Integrations
                </Button>
                {/* Retry with the SAME identity the flow started with. The
                    state JWT is the target user's; localStorage holds whoever
                    is logged into this browser. When an admin connects Google
                    on behalf of a team member and the exchange fails, retrying
                    from localStorage silently connects the ADMIN's account
                    instead - and it looks like it worked. */}
                <Button
                  size="sm"
                  onClick={() => {
                    const t = new URLSearchParams(window.location.search).get('state')
                      || localStorage.getItem('token');
                    window.location.href = `${API_URL}/api/google-auth/auth-url?token=${encodeURIComponent(t || '')}`;
                  }}
                >
                  Try again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
